<template>
    <Suspense>
        <template #default>
            <router-view></router-view>
        </template>
        <template #fallback>
            <Spinner
                type="complex"
                :color="`text-blue-600`"
                :color2="`text-blue-400`"
                :text-color="`text-gray-200`"
                :overlay="true"
                :speed="1000"
                :dots="true"
                :dot-char="'>'"
                :dot-dual="true"
                :dot-reverse="true"
                :text="t('loading')"
                :inline="false"
                :pulse="true"
            />
        </template>
    </Suspense>

    <Toasts />

    <WorkerIndicator
        :key="domKey"
        class="beam fixed top-1 left-1 z-8000 flex w-12 place-content-center"
        :class="workerIndicator === true ? 'flex' : 'hidden'"
    />
</template>

<script setup>
    import { ref, inject, onMounted } from 'vue';
    import Spinner from './ui/Spinner.vue';
    import Toasts from './ui/Toasts.vue';
    import WorkerIndicator from './components/WorkerIndicator.vue';
    import { keeper, getRndKey } from './composables/utils';
    import refreshWorker from './refresh.worker.js?worker';

    const config = inject('config');

    const workerIndicator = ref(false);
    const domKey = ref(`a-${getRndKey()}`);
    const svc = new refreshWorker();

    const workerIndicatorOn = () => {
        workerIndicator.value = true;
        domKey.value = `a-${getRndKey()}`;
    };

    const workerIndicatorOff = () => {
        workerIndicator.value = false;
        domKey.value = `a-${getRndKey()}`;
    };

    const workerIndicatorBlink = (timeout = 4000) => {
        workerIndicatorOn();
        setTimeout(async function () {
            workerIndicatorOff();
        }, timeout);
    };

    const refreshTokenSvc = async () => {
        workerIndicatorBlink(2000);

        await svc.postMessage({
            apiUrl: keeper('apiUrl'),
            sessionToken: keeper('sessionToken'),
            fileStorageApiUrl: keeper('fileStorageApiUrl'),
            sessionFsToken: keeper('sessionFsToken'),
            fileStorageEnabled: config.env.fileStorageEnabled,
        });
    };

    svc.onmessage = async (e) => {
        const sessionToken = await e.data.sessionToken;
        const sessionFsToken = await e.data.sessionFsToken;
        keeper('sessionToken', sessionToken || '');
        keeper('sessionFsToken', sessionFsToken || '');
        setTimeout(async function () {
            if (sessionToken !== false) {
                refreshTokenSvc();
            }
        }, 40000);
    };

    onMounted(async () => {
        await refreshTokenSvc();
    });
</script>

<style>
    #app {
        -webkit-font-smoothing: antialiased;
        -moz-osx-font-smoothing: grayscale;
    }

    input:autofill {
        filter: none;
        background: none; /* or any other */
    }

    :-moz-autofill,
    :-moz-autofill-preview {
        filter: none;
        background: none;
    }

    * {
        --scrollbarBG: rgb(255, 255, 255);
        --thumbBG: rgb(160, 160, 160);
        scrollbar-width: thin;
        scrollbar-color: var(--thumbBG) var(--scrollbarBG);
    }

    *::-webkit-scrollbar {
        width: 12px;
    }

    *::-webkit-scrollbar-track {
        background: var(--scrollbarBG);
    }

    *::-webkit-scrollbar-thumb {
        background-color: var(--thumbBG);
        border-radius: 5px;
        border: 2px solid var(--scrollbarBG);
    }

    .v3dp__datepicker {
        --vdp-bg-color: #ffffff;
        --vdp-text-color: #000000;
        --vdp-box-shadow:
            0 4px 10px 0 rgba(128, 144, 160, 0.1), 0 0 1px 0 rgba(128, 144, 160, 0.81);
        --vdp-border-radius: 3px;
        --vdp-heading-size: 2.5em;
        --vdp-heading-weight: bold;
        --vdp-heading-hover-color: #eeeeee;
        --vdp-arrow-color: #000000;
        --vdp-elem-color: #000000;
        --vdp-disabled-color: #d5d9e0;
        --vdp-hover-color: #ffffff;
        --vdp-hover-bg-color: #4338ca;
        --vdp-selected-color: #ffffff;
        --vdp-selected-bg-color: #4338ca;
        --vdp-elem-font-size: 0.8em;
        --vdp-elem-border-radius: 3px;
        --vdp-divider-color: #d5d9e0;
    }

    .fade-enter-active,
    .fade-leave-active {
        transition: opacity 0.5s ease;
    }

    .fade-enter,
    .fade-leave-active {
        opacity: 0;
    }

    .child-view {
        position: absolute;
        width: 100%;
        left: 0;
        transition: all 0.5s cubic-bezier(0.55, 0, 0.1, 1);
    }

    .slide-left-enter,
    .slide-right-leave-active {
        opacity: 0;
        -webkit-transform: translate(30px, 0);
        transform: translate(30px, 0);
    }

    .slide-left-leave-active,
    .slide-right-enter {
        opacity: 0;
        -webkit-transform: translate(-30px, 0);
        transform: translate(-30px, 0);
    }
</style>
